import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
    Warehouse, ChevronDown, Search, BarChart3, Clock, CheckCircle2,
    Layers, AlertTriangle, Filter, CalendarDays
} from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_OPTIONS = [
    { value: 'en_attente', label: 'En attente', dot: 'bg-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
    { value: 'entree_reservee', label: 'Entree reservee', dot: 'bg-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
    { value: 'sortie_planifiee', label: 'Sortie planifiee', dot: 'bg-blue-500', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
];

export default function GardeMeuble() {
    const navigate = useNavigate();
    const [allItems, setAllItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [openDrop, setOpenDrop] = useState(null);

    const fetchItems = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/garde-meuble-list`, { withCredentials: true });
            setAllItems(res.data);
        } catch {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchItems(); }, []);

    const stats = useMemo(() => {
        const s = { total: allItems.length, en_attente: 0, entree_reservee: 0, sortie_planifiee: 0, urgent: 0 };
        const now = new Date();
        allItems.forEach(i => {
            if (i.statut === 'en_attente') s.en_attente++;
            else if (i.statut === 'entree_reservee') s.entree_reservee++;
            else if (i.statut === 'sortie_planifiee') s.sortie_planifiee++;
            // Urgent logic
            if (i.date_entree && i.statut !== 'entree_reservee' && i.statut !== 'sortie_planifiee') {
                const entreeDate = new Date(i.date_entree + 'T00:00');
                const daysLeft = Math.ceil((entreeDate - now) / (1000 * 60 * 60 * 24));
                if (daysLeft <= 7) { s.urgent++; i._urgent = true; return; }
            }
            if (i.date_sortie && i.statut !== 'sortie_planifiee') {
                const sortieDate = new Date(i.date_sortie + 'T00:00');
                const daysLeft = Math.ceil((sortieDate - now) / (1000 * 60 * 60 * 24));
                if (daysLeft <= 7) { s.urgent++; i._urgent = true; return; }
            }
        });
        return s;
    }, [allItems]);

    const filteredItems = useMemo(() => {
        let items = [...allItems];
        if (statusFilter) items = items.filter(i => i.statut === statusFilter);
        if (search) {
            const q = search.toLowerCase();
            items = items.filter(i => (i.client_nom || '').toLowerCase().includes(q));
        }
        items.sort((a, b) => {
            if ((b._urgent ? 1 : 0) !== (a._urgent ? 1 : 0)) return (b._urgent ? 1 : 0) - (a._urgent ? 1 : 0);
            const da = a.date_entree ? new Date(a.date_entree) : new Date('9999-12-31');
            const db = b.date_entree ? new Date(b.date_entree) : new Date('9999-12-31');
            return da - db;
        });
        return items;
    }, [allItems, search, statusFilter]);

    const handleStatutChange = async (clientId, newStatut) => {
        setOpenDrop(null);
        try {
            await axios.post(`${API_URL}/api/clients/${clientId}/garde-meuble/statut`, { statut: newStatut }, { withCredentials: true });
            toast.success('Statut mis a jour');
            fetchItems();
        } catch {
            toast.error('Erreur');
        }
    };

    const handleDateSortie = async (clientId, date) => {
        try {
            await axios.post(`${API_URL}/api/clients/${clientId}/garde-meuble/statut`, { date_sortie: date }, { withCredentials: true });
            // Update local state
            setAllItems(prev => prev.map(i => i.client_id === clientId ? { ...i, date_sortie: date } : i));
        } catch {
            toast.error('Erreur');
        }
    };

    const handleAdresseGm = async (clientId, value) => {
        // Update local state immediately for responsive UI
        setAllItems(prev => prev.map(i => i.client_id === clientId ? { ...i, adresse_gm: value } : i));
        try {
            await axios.post(`${API_URL}/api/clients/${clientId}/garde-meuble/statut`, { adresse_gm: value }, { withCredentials: true });
        } catch {
            toast.error('Erreur enregistrement adresse');
        }
    };

    // Debounced save when typing freely (no list pick)
    const adresseTimers = useRef({});
    const handleAdresseGmTyping = (clientId, value) => {
        // local update
        setAllItems(prev => prev.map(i => i.client_id === clientId ? { ...i, adresse_gm: value } : i));
        // debounce backend save
        if (adresseTimers.current[clientId]) clearTimeout(adresseTimers.current[clientId]);
        adresseTimers.current[clientId] = setTimeout(() => {
            axios.post(`${API_URL}/api/clients/${clientId}/garde-meuble/statut`, { adresse_gm: value }, { withCredentials: true })
                .catch(() => toast.error('Erreur enregistrement adresse'));
        }, 1200);
    };

    const STAT_BLOCKS = [
        { label: 'Total', value: stats.total, icon: BarChart3, borderCls: 'border-gray-200', iconCls: 'text-gray-500', valueCls: 'text-gray-800' },
        { label: 'En attente', value: stats.en_attente, icon: Clock, borderCls: 'border-amber-200', iconCls: 'text-amber-500', valueCls: 'text-amber-600' },
        { label: 'Entree reservee', value: stats.entree_reservee, icon: CheckCircle2, borderCls: 'border-emerald-200', iconCls: 'text-emerald-500', valueCls: 'text-emerald-600' },
        { label: 'Sortie planifiee', value: stats.sortie_planifiee, icon: Layers, borderCls: 'border-blue-200', iconCls: 'text-blue-500', valueCls: 'text-blue-600' },
        { label: 'Urgent', value: stats.urgent, icon: AlertTriangle, borderCls: 'border-pink-200', iconCls: 'text-pink-500', valueCls: 'text-pink-600' },
    ];

    return (
        <Layout>
            <div className="space-y-5" data-testid="garde-meuble-page">
                <div>
                    <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Garde-meuble</h1>
                    <p className="text-sm text-gray-400 mt-0.5">Client Stockage</p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-5 gap-3" data-testid="gm-stats">
                    {STAT_BLOCKS.map((b, i) => (
                        <div key={i} className={`bg-white rounded-xl border-2 ${b.borderCls} p-4 flex flex-col gap-1`}>
                            <div className="flex items-center gap-2">
                                <b.icon className={`w-4 h-4 ${b.iconCls}`} />
                                <span className={`text-2xl font-bold ${b.valueCls}`}>{b.value}</span>
                            </div>
                            <span className="text-xs text-gray-500">{b.label}</span>
                        </div>
                    ))}
                </div>

                {/* Search + Filter */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input placeholder="Rechercher (nom)..." value={search} onChange={e => setSearch(e.target.value)}
                            className="pl-9 text-sm" data-testid="gm-search" />
                    </div>
                    <div className="relative min-w-[180px]">
                        <Filter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 appearance-none"
                            data-testid="gm-status-filter">
                            <option value="">Tous les statuts</option>
                            {STATUT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                        <Warehouse className="w-4 h-4 text-[#1B365D]" />
                        <h2 className="text-sm font-bold text-[#1B365D]">Liste des garde-meubles ({filteredItems.length})</h2>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1B365D]" /></div>
                    ) : filteredItems.length === 0 ? (
                        <div className="text-center py-12">
                            <Warehouse className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                            <p className="text-gray-400 text-sm">Aucun garde-meuble trouve</p>
                        </div>
                    ) : (
                        <div>
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-100">
                                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Client</th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Date d'entree</th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Tarif mensuel</th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Duree</th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Date de sortie</th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Adresse GM</th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Statut</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredItems.map((item, idx) => {
                                        const currentStatut = item.statut || 'en_attente';
                                        const sCfg = STATUT_OPTIONS.find(o => o.value === currentStatut) || STATUT_OPTIONS[0];
                                        const dropKey = `gm-${item.client_id}`;
                                        return (
                                            <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`gm-row-${idx}`}>
                                                <td className="px-5 py-3.5">
                                                    <div className="flex items-center gap-2">
                                                        <button onClick={() => navigate(`/clients/${item.client_id}`)} className="text-sm font-semibold text-[#1B365D] hover:underline">
                                                            {item.client_nom}
                                                        </button>
                                                        {item._urgent && (
                                                            <Badge className="bg-red-500 text-white text-[9px] px-1.5 py-0.5 font-bold animate-pulse">URGENT</Badge>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3.5">
                                                    <div className="flex items-center gap-1.5 text-sm text-gray-500">
                                                        <CalendarDays className="w-3 h-3 text-gray-400" />
                                                        {item.date_entree ? new Date(item.date_entree + 'T00:00').toLocaleDateString('fr-FR') : '-'}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3.5 text-sm text-gray-700 font-medium">{item.tarif_mensuel || 0}</td>
                                                <td className="px-4 py-3.5 text-sm text-gray-600">{item.duree || '-'}</td>
                                                <td className="px-4 py-3.5">
                                                    {item.date_sortie_auto ? (
                                                        <div
                                                            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-2.5 py-1 text-sm"
                                                            title="Date issue de la programmation (jour typé Sortie garde-meuble)"
                                                            data-testid={`gm-date-sortie-${idx}`}
                                                        >
                                                            <CalendarDays className="w-3 h-3" />
                                                            <span className="font-medium">{new Date(item.date_sortie + 'T00:00').toLocaleDateString('fr-FR')}</span>
                                                            <span className="text-[10px] uppercase tracking-wide opacity-70">auto</span>
                                                        </div>
                                                    ) : (
                                                        <input type="date" value={item.date_sortie || ''}
                                                            onChange={e => handleDateSortie(item.client_id, e.target.value)}
                                                            className="border border-gray-200 rounded-lg px-2 py-1 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                                            data-testid={`gm-date-sortie-${idx}`} />
                                                    )}
                                                </td>
                                                <td className="px-4 py-3.5 min-w-[260px]">
                                                    <AddressAutocomplete
                                                        value={item.adresse_gm || ''}
                                                        onChange={(v) => handleAdresseGmTyping(item.client_id, v)}
                                                        onSelect={(picked) => handleAdresseGm(item.client_id, picked.adresse || picked.display || '')}
                                                        placeholder="Adresse du garde-meuble..."
                                                        inputClassName="text-sm h-8"
                                                        testId={`gm-adresse-${idx}`}
                                                    />
                                                </td>
                                                <td className="px-4 py-3.5">
                                                    <div className="relative">
                                                        <button onClick={() => setOpenDrop(openDrop === dropKey ? null : dropKey)}
                                                            className={`flex items-center gap-2 ${sCfg.bg} ${sCfg.border} border rounded-xl px-3 py-1.5 text-xs font-semibold shadow-sm hover:shadow-md transition-all min-w-[130px]`}>
                                                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sCfg.dot}`} />
                                                            <span className={`${sCfg.text} flex-1 text-left`}>{sCfg.label}</span>
                                                            <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${openDrop === dropKey ? 'rotate-180' : ''}`} />
                                                        </button>
                                                        {openDrop === dropKey && (
                                                            <>
                                                                <div className="fixed inset-0 z-30" onClick={() => setOpenDrop(null)} />
                                                                <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-40 min-w-[160px]">
                                                                    {STATUT_OPTIONS.map(o => (
                                                                        <button key={o.value} onClick={() => handleStatutChange(item.client_id, o.value)}
                                                                            className={`w-full text-left px-4 py-2 text-xs flex items-center gap-3 transition-colors ${currentStatut === o.value ? 'bg-gray-50 font-bold' : 'hover:bg-gray-50'}`}>
                                                                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${o.dot}`} />
                                                                            <span className="text-gray-700 flex-1">{o.label}</span>
                                                                            {currentStatut === o.value && <span className="text-[#1B365D]">&#10003;</span>}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
